'use client'

import type { Control, FieldErrorsImpl } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import { cn } from '@/utilities/ui'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Controller } from 'react-hook-form'

import { Error } from '../Error'
import { Width } from '../Width'

interface AddressSuggestion {
  sla: string
  score: number
  pid: string
}

export const Address: React.FC<{
  name: string
  control: Control
  errors: Partial<FieldErrorsImpl>
  label?: string
  required?: boolean
  width?: number | string
  usePlaceholders?: boolean
}> = ({ name, control, errors, label, required, width, usePlaceholders }) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const fetchSuggestions = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 3) {
      setSuggestions([])
      setIsOpen(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/address-lookup?q=${encodeURIComponent(query)}`)
        const data: AddressSuggestion[] = await res.json()
        setSuggestions(data)
        setIsOpen(data.length > 0)
        setHighlightIndex(-1)
      } catch {
        setSuggestions([])
        setIsOpen(false)
      }
    }, 300)
  }, [])

  return (
    <Width width={width}>
      <Label htmlFor={name} className={usePlaceholders ? 'sr-only' : undefined}>
        {label}
        {required && (
          <span className="required">
            * <span className="sr-only">(required)</span>
          </span>
        )}
      </Label>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => {
          const handleSelect = (sla: string) => {
            onChange(sla)
            setSuggestions([])
            setIsOpen(false)
          }

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setHighlightIndex((prev) => Math.max(prev - 1, -1))
            } else if (e.key === 'Enter') {
              if (highlightIndex >= 0 && suggestions[highlightIndex]) {
                e.preventDefault()
                handleSelect(suggestions[highlightIndex].sla)
              }
            } else if (e.key === 'Escape') {
              setIsOpen(false)
            }
          }

          const isListOpen = isOpen && suggestions.length > 0
          const hasError = Boolean(errors[name])

          return (
            <div ref={wrapperRef} className="relative">
              {/* Figma Combobox 2198:36869 (instance 2229:26574) — this field is a Combobox,
                  not the filled Input primitive: a white Menu surface with a 1px theme/border,
                  radius `sm` 2px and shadow-xs, whose first child is the input row
                  (padding/p-3 12 horizontal, padding/p-2,5 10 vertical).
                  2229:26574 measures 36px tall in total. Figma draws the stroke INSIDE the
                  frame where CSS adds it outside, so 10 + 16 + 10 + 2 would overshoot by 2px —
                  the row is pinned to h-9 (36) instead, matching the plain Inputs beside it.
                  The trailing search glyph is bound to icon/icon-secondary #ffffff (invisible
                  on the white surface) and is absent from the rendered node, so it is not
                  drawn. Error state: 1px theme/destructive #ef4444, as on Input 2198:38531. */}
              <div
                className={cn(
                  'h-9 rounded-sm border bg-background shadow-xs focus-within:ring-[3px] focus-within:ring-ring',
                  hasError ? 'border-destructive' : 'border-border',
                )}
              >
                <div className="flex h-full items-center px-3">
                  <input
                    id={name}
                    type="text"
                    aria-invalid={hasError || undefined}
                    className="min-w-0 flex-1 bg-transparent text-body-sm font-light text-foreground placeholder:text-muted-foreground placeholder:font-light focus:outline-none aria-invalid:text-destructive"
                    placeholder={usePlaceholders ? `${label}${required ? '*' : ''}` : undefined}
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value)
                      fetchSuggestions(e.target.value)
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      if (suggestions.length > 0) setIsOpen(true)
                    }}
                    autoComplete="off"
                  />
                </div>
              </div>
              {/* Menu Group 2198:36876 — the item list is attached to the input row with no
                  gap, sharing a single 1px theme/border divider (hence the -1px offset). */}
              {isListOpen && (
                <ul className="absolute z-50 top-full left-0 right-0 -mt-px max-h-64 overflow-y-auto rounded-b-sm border border-border bg-background p-1 shadow-xs">
                  {suggestions.map((suggestion, i) => (
                    <li
                      key={suggestion.pid}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleSelect(suggestion.sla)
                      }}
                      onMouseEnter={() => setHighlightIndex(i)}
                      // Menu Item 2198:36855 — h32, padding/p-2 8 × padding/p-1,5 6, gap-2,
                      // radius `sm`, Body/bodySm. State=Hover 2198:36860 fills theme/secondary
                      // and keeps the label theme/foreground.
                      className={cn(
                        'flex h-8 cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-[length:var(--text-body-sm)] leading-[var(--text-body-sm--line-height)] font-light text-foreground',
                        i === highlightIndex && 'bg-secondary',
                      )}
                    >
                      <span className="min-w-0 truncate">{suggestion.sla}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        }}
        rules={{ required }}
      />
      {errors[name] && <Error name={name} />}
    </Width>
  )
}
